import { useQuery } from '@apollo/client'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useEffect, useState, type PropsWithChildren } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  GET_SESSION,
  screenPath,
  setSessionData,
  toPriceText,
  type ISession,
  type ISessionVariables,
} from '@commerce/core'
import { YALO_SEMANTIC } from '@commerce/tokens'
import { VisitSheet } from '../components/VisitSheet'
import { Screen } from '../design'
import { WEEKDAYS, useDemo } from '../demo'
import { useStorefrontConfig } from '../config/StorefrontConfigContext'
import { useAppDispatch } from '../hooks/useAppDispatch'
import { useExpireSession } from '../hooks/useExpireSession'
import { usePresentationContext } from '../hooks/usePresentationContext'
import { BottomNav } from '../components/BottomNav'

/** The design draws a two-letter monogram where a logo would go. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('') || '?'

/**
 * How much of the credit line is still available, as a percentage.
 *
 * Clamped and guarded because both numbers come from a fixture today and from a
 * backend eventually, and a line of 0 would otherwise render `NaN%` — which
 * `LinearProgress` reports as a React warning rather than as a bar.
 *
 * This is the one piece of arithmetic on the screen. It belongs in a view-model
 * in `@commerce/core` the moment credit becomes real; flagged to the lead.
 */
const creditPercent = (credit: number, line: number): number =>
  line <= 0 ? 0 : Math.max(0, Math.min(100, (credit / line) * 100))

const CardBlock = ({ children }: PropsWithChildren) => (
  <Card variant="outlined" sx={{ p: 2 }}>
    {children}
  </Card>
)

/**
 * The account screen.
 *
 * It is the one screen in this group that reads anything real: the store code
 * and the phone number are the session's customer, from `GET_SESSION`. The
 * distributor, the visit day and the credit line are demo state — commerce-scs
 * has no endpoint for any of them.
 *
 * The session is supplementary here rather than load-bearing, so neither its
 * loading nor its failure blanks the screen: the visit-day control and the
 * distributor do not depend on it, and a shopper who opened this screen to
 * change their visit day should not be blocked by an unrelated request. The
 * session is still dispatched into the store when it lands, for the same reason
 * the catalog page does it — it is what makes the per-customer hide-prices rule
 * resolvable, and this screen shows money.
 */
export const AccountPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { expireSession, loading: expiring } = useExpireSession()
  const presentation = usePresentationContext()
  const { storeName } = useStorefrontConfig()
  const { distributor, visitDay, visitConfirmed, confirmVisitDay } = useDemo()
  const [sheetOpen, setSheetOpen] = useState(false)

  const { data, loading, error } = useQuery<ISession, ISessionVariables>(GET_SESSION, {
    variables: { storefrontName: slug, sessionUid: sessionId },
  })

  const session = data?.session
  useEffect(() => {
    if (session) dispatch(setSessionData(session))
  }, [dispatch, session])

  const customer = session?.customer
  const storeCode = loading ? 'Cargando…' : (customer?.code ?? '—')
  // `phoneNumber` is a list on the session; the design shows one line.
  const phone = loading ? 'Cargando…' : (customer?.phoneNumber?.[0] ?? '—')

  const creditText = toPriceText(distributor.credit, presentation)
  const creditLineText = toPriceText(distributor.creditLine, presentation)

  return (
    <Screen testId="account-page">
      <Box sx={{ px: 2.5, pt: 1.75, pb: 1.25 }}>
        <Typography variant="h3" component="h1">
          Mi cuenta
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={{ px: 2.5, pb: 4 }}>
        {error && (
          <Alert severity="warning">
            No pudimos cargar los datos de tu tienda: {error.message}
          </Alert>
        )}

        <CardBlock>
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Avatar
              // Decorative: the store name it is built from is right beside it,
              // so announcing the initials again would only be noise.
              aria-hidden
              sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontWeight: 700 }}
            >
              {initialsOf(storeName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700}>
                {storeName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {storeCode} · {distributor.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {phone}
              </Typography>
            </Box>
          </Stack>
        </CardBlock>

        <CardBlock>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="overline" color="text.secondary">
                Día de visita
              </Typography>
              {visitConfirmed && (
                <Chip
                  size="small"
                  label="Confirmado"
                  sx={{
                    height: 22,
                    fontSize: 11.5,
                    bgcolor: alpha(YALO_SEMANTIC.statusSuccess, 0.2),
                    color: '#0B6B4C',
                  }}
                />
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Tu asesora visita tu tienda cada semana. Elige el día que te acomoda.
            </Typography>

            <Typography variant="body1" fontWeight={600}>
              {visitDay ?? 'Sin día elegido'}
            </Typography>

            <Button variant={visitDay ? 'outlined' : 'contained'} onClick={() => setSheetOpen(true)}>
              {visitDay ? 'Cambiar día de visita' : 'Elegir día de visita'}
            </Button>
          </Stack>
        </CardBlock>

        {/* Hidden wholesale when the storefront hides prices: a credit line is a
            price, and half a card with an empty amount is not a graceful
            degradation. */}
        {creditText && (
          <CardBlock>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                Crédito con {distributor.name}
              </Typography>
              <Typography variant="h2" component="p">
                {creditText}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={creditPercent(distributor.credit, distributor.creditLine)}
                aria-label="Crédito disponible"
                sx={{ height: 6, borderRadius: 999, bgcolor: YALO_SEMANTIC.bgSunken }}
              />
              <Typography variant="body2" color="text.secondary">
                Línea total {creditLineText} · corte los viernes
              </Typography>
            </Stack>
          </CardBlock>
        )}

        {/* Ends the session, then returns to the session-free login. Both
            halves are load-bearing: leaving the session ACTIVE means the next
            login reuses it (same cart, same selections), and returning to a
            session-scoped URL would carry the dead id straight back in. */}
        <Button
          variant="outlined"
          color="error"
          disabled={expiring}
          onClick={async () => {
            await expireSession(slug, sessionId)
            navigate(screenPath('home', { slug }))
          }}
        >
          Cerrar sesión
        </Button>
      </Stack>

      <VisitSheet
        open={sheetOpen}
        days={WEEKDAYS}
        selectedDay={visitDay}
        onClose={() => setSheetOpen(false)}
        onConfirm={(day) => {
          confirmVisitDay(day)
          setSheetOpen(false)
        }}
      />

      <BottomNav slug={slug} sessionId={sessionId} />
    </Screen>
  )
}
